import React, { useState } from 'react';
import { notification, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import type { TablePaginationConfig, TableProps } from 'antd';
import {
  IProduct,
  ISearchProductReq,
  ISearchProductRes,
} from '@/interfaces/product';
import {
  EditFilled,
  DeleteFilled,
  CheckCircleFilled,
  CloseCircleFilled,
} from '@ant-design/icons';
import { deleteProduct } from '@/adapters/product';
import { IDataModal } from '@/interfaces/dataModal';
import DeleteConfirmModal from '@/components/modal/confirm-delete';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import TableComponent from '@/components/table';
import { formatNumber } from '@/utils/dataUtils';
import { isFullAccess } from '@/utils/permissionUtils';

interface IProps {
  loading: boolean;
  criteria: ISearchProductReq;
  product: ISearchProductRes;
  onDelete: (id: number) => void;
  onEditData: (item: IProduct) => void;
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IProduct> | SorterResult<IProduct>[],
  ) => void;
}

const _productModal: IDataModal<IProduct> = {
  open: false,
};

const ProductTable = ({
  loading,
  criteria,
  product,
  onDelete,
  onTableChange,
}: IProps) => {
  const perFullAccess = isFullAccess();
  const navigate = useNavigate();
  const [deleteModal, setDeleteModal] =
    useState<IDataModal<IProduct>>(_productModal);

  const onDeleteProduct = async () => {
    try {
      await deleteProduct(Number(deleteModal?.id));
      notification.success({
        message: 'ลบสินค้าสำเร็จ',
      });
      onDelete(Number(deleteModal?.id || 0));
      setDeleteModal({ ..._productModal });
    } catch (error) {
      console.log(error);
    }
  };

  const columns: TableProps<IProduct>['columns'] = [
    {
      title: 'รหัส',
      dataIndex: 'code',
      key: 'code',
      className: 'font-bold',
      width: 130,
      align: 'center',
      render: (_, rec) => (
        <Space
          onClick={() => navigate(`/product/${rec.id}`)}
          className="text-blue-700 underline underline-offset-2 cursor-pointer"
        >
          {_}
        </Space>
      ),
    },
    {
      sorter: true,
      title: 'สินค้า',
      dataIndex: 'name',
      key: 'name',
      className: 'font-bold',
    },

    {
      title: 'หน่วย Package',
      dataIndex: 'packageUnitName',
      key: 'packageUnitName',
      align: 'center',
      width: 120,
    },
    {
      title: 'Conversion',
      dataIndex: 'conversion',
      key: 'conversion',
      align: 'right',
      width: 120,
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'หน่วยขาย',
      dataIndex: 'baseUnitName',
      key: 'baseUnitName',
      align: 'center',
      width: 120,
    },
    {
      sorter: true,
      title: criteria.iSample ? 'ต้นทุน' : 'ราคาขาย',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      className: 'font-bold text-blue-600',
      align: 'right',
      width: 120,
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'Safety Stock',
      dataIndex: 'safetyStock',
      key: 'safetyStock',
      className: 'font-bold text-green-700',
      align: 'right',
      width: 130,
      render: (_: number) => formatNumber(_, 2),
    },
    // {
    //   title: 'BU',
    //   dataIndex: 'businessUnitName',
    //   key: 'businessUnitName',
    //   align: 'center',
    //   width: 80,
    // },
    {
      title: 'สถานะ',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      align: 'center',
      render: (_: boolean) => {
        return _ ? (
          <CheckCircleFilled className="text-green-600" />
        ) : (
          <CloseCircleFilled className="text-red-600" />
        );
      },
    },
    {
      dataIndex: 'action',
      key: 'action',
      width: 80,
      hidden: !perFullAccess,
      render: (_, rec) => (
        <div className="flex flex-row divide-x divide-dashed items-center w-full justify-center">
          <DeleteFilled
            className="text-red-700 px-1.5"
            onClick={() =>
              setDeleteModal({
                open: true,
                item: rec,
                id: rec.id,
              })
            }
          />
          <EditFilled
            className="text-blue-500 px-1.5"
            onClick={() => navigate(`/product/${rec.id}`)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<IProduct>
        columns={columns}
        criteria={criteria}
        data={product}
        loading={loading}
        onTableChange={onTableChange}
      />
      <DeleteConfirmModal
        content={`คุณต้องการลบลูกค้า '${deleteModal?.item?.name}' ใช่หรือไม่`}
        handleCancel={() => {
          setDeleteModal({ ..._productModal });
        }}
        handleOk={() => {
          onDeleteProduct();
        }}
        open={deleteModal?.open}
        title={`ลูกค้า '${deleteModal?.item?.name}'`}
      />
    </div>
  );
};

export default ProductTable;
